import {useCallback, useEffect, useRef, useState} from 'react';
import {ChatSession, ChatState, SessionStatus} from '../types/chat';
import chatService from '../services/chatService';
import {useAuth} from '../contexts/AuthContext';
import {usePageVisibility} from './usePageVisibility';

const ACTIVE_SESSION_RUNTIME_STATUSES = [
    'QUEUED',
    'PROCESSING',
    'WAITING_AGENT_CALLS',
    'READY_TO_RESUME',
    'RESUMING',
    'INTERRUPTED'
];

const shouldKeepPollingRuntime = (runtime?: string | null): boolean =>
    ACTIVE_SESSION_RUNTIME_STATUSES.includes(runtime || '');

const HIDDEN_MESSAGE_KINDS = [
    'STARTING_GUN_CALL',
    'STARTING_GUN_RESULT',
    'TOOL_CALL',
    'TOOL_RESULT',
    'OUTPUT_REPAIR_FEEDBACK',
    'SYSTEM_PROMPT',
];

const VISIBLE_OUTPUT_KINDS = [
    'USER_INPUT',
    'CHAT_TEXT_OUTPUT',
    'CONTEXT_INPUT',
    'RESUME_INPUT',
];

// 判断是否为真正的assistant消息（非任务规划、非内部函数调用）
const isRealAssistantMessage = (msg: any): boolean => {
    if (null == msg || 'assistant' !== msg.role) return false;
    if (null != msg.messageKind) {
        if (HIDDEN_MESSAGE_KINDS.includes(msg.messageKind)) return false;
        if (VISIBLE_OUTPUT_KINDS.includes(msg.messageKind)) return true;
    }
    // __suspended__ 标记的挂起提示消息不算真实回复，继续轮询
    if ('__suspended__' === msg.functionName) return false;
    // __sub_task_result__ 标记的自调用子任务最终输出不算主会话真实回复，继续轮询
    if ('__sub_task_result__' === msg.functionName) return false;
    const content = msg.content || '';
    if (content.startsWith('[function_call]')
        || content.startsWith('[starting_gun]')
        || content.startsWith('[output_validation]')
        || content.startsWith('[output_validation_aborted]')) {
        return false;
    }
    const cleaned = content.replace(/```[\s\S]*?```/g, '').trim();
    if (/^\s*\{\s*"planId"\s*:\s*"[^"]*"\s*\}\s*$/.test(cleaned)) {
        return false;
    }
    return true;
};

// 添加调试日志
// console.log('useChat hook loaded');

export const useChat = () => {
    const {user} = useAuth();
    const isPageVisible = usePageVisibility();
    const [state, setState] = useState<ChatState>({
        sessions: [],
        currentSession: null,
        messages: [],
        isLoading: false,
        isConnected: false,
        error: null
    });

    // 分页相关状态
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMoreSessions, setHasMoreSessions] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [sessionAgentId, setSessionAgentId] = useState<number | undefined>(undefined);

    // 子会话状态
    const [childSessions, setChildSessions] = useState<ChatSession[]>([]);

    // 轮询状态
    const [isSuspended, setIsSuspended] = useState(false);
    const [interrupted, setInterrupted] = useState(false);
    const [sessionRuntimeStatus, setSessionRuntimeStatus] = useState<string | null>(null);
    const [pollingTick, setPollingTick] = useState(0);
    const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollingCountRef = useRef(0);
    const MAX_POLL_COUNT = 300;
    const messagesRef = useRef<typeof state.messages>(state.messages);
    const resumeRequestRef = useRef<{ sessionId: number; promise: Promise<SessionStatus> } | null>(null);
    const previousPageVisibleRef = useRef(isPageVisible);

    // 导航栈 — 进入子会话前保存父会话，支持返回
    const [navigationStack, setNavigationStack] = useState<ChatSession[]>([]);

    // 加载子智能体会话
    const loadChildSessions = useCallback(async (sessionId: number) => {
        try {
            const children = await chatService.getChildSessions(sessionId);
            setChildSessions(children);
        } catch {
            setChildSessions([]);
        }
    }, []);

    // 获取当前用户ID，未登录返回null
    const getCurrentUserId = useCallback((): string | null => {
        return user?.id ? String(user.id) : null;
    }, [user?.id]);

    // 加载用户会话列表（重置分页）
    const loadSessions = useCallback(async (userId?: string, agentId?: number) => {
        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录', isLoading: false}));
            return;
        }
        try {
            setState(prev => ({...prev, isLoading: true, error: null}));
            // 重置分页状态
            setCurrentPage(1);
            setSessionAgentId(agentId);
            setHasMoreSessions(true);
            // 根据agentId判断sessionType：有agentId则查AGENT类型，否则查NORMAL类型
            const sessionType = agentId ? 'AGENT' : 'NORMAL';
            // 调用分页接口，获取第一页数据
            const pageResult = await chatService.getUserSessions(actualUserId, agentId, sessionType, 1, 20);
            setState(prev => ({
                ...prev,
                sessions: pageResult.records,
                isLoading: false
            }));
            setHasMoreSessions(pageResult.hasNext);
            setCurrentPage(pageResult.page + 1); // 下一页 = 当前页 + 1（page 从 0 开始）
        } catch (error) {
            // console.error('加载会话列表失败:', error);
            setState(prev => ({
                ...prev,
                error: '加载会话列表失败',
                isLoading: false
            }));
        }
    }, [getCurrentUserId]);

    // 加载更多会话（加载下一页）
    const loadMoreSessions = useCallback(async () => {
        if (loadingMore || !hasMoreSessions) {
            return;
        }
        const actualUserId = getCurrentUserId();
        if (!actualUserId) {
            return;
        }
        try {
            setLoadingMore(true);
            // 根据sessionAgentId判断sessionType：有则查AGENT类型，否则查NORMAL类型
            const sessionType = sessionAgentId ? 'AGENT' : 'NORMAL';
            const pageResult = await chatService.getUserSessions(actualUserId, sessionAgentId, sessionType, currentPage, 20);
            // 追加数据到现有 sessions 列表
            setState(prev => ({
                ...prev,
                sessions: [...prev.sessions, ...pageResult.records],
            }));
            setHasMoreSessions(pageResult.hasNext);
            setCurrentPage(pageResult.page + 1);
        } catch (error) {
            // console.error('加载更多会话失败:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [getCurrentUserId, loadingMore, hasMoreSessions, currentPage, sessionAgentId]);

    // 创建新会话
    const createSession = useCallback(async (sessionName?: string, userId?: string, agentId?: number) => {
        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录'}));
            throw new Error('请先登录');
        }
        try {
            setState(prev => ({...prev, isLoading: true, error: null}));
            const newSession = await chatService.createNewSession(sessionName, actualUserId, agentId);
            setState(prev => ({
                ...prev,
                sessions: [newSession, ...(Array.isArray(prev.sessions) ? prev.sessions : [])],
                isLoading: false
            }));
            return newSession;
        } catch (error) {
            // console.error('创建会话失败:', error);
            setState(prev => ({
                ...prev,
                error: '创建会话失败',
                isLoading: false
            }));
            throw error;
        }
    }, [getCurrentUserId]);

    // 加载会话消息
    const loadMessages = useCallback(async (sessionId: number) => {
        try {
            setState(prev => ({...prev, isLoading: true, error: null}));
            const userId = getCurrentUserId();
            const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
            setState(prev => ({
                ...prev,
                messages,
                isLoading: false
            }));
            return messages;
        } catch (error) {
            // console.error('加载消息失败:', error);
            setState(prev => ({
                ...prev,
                error: '加载消息失败',
                isLoading: false
            }));
            throw error;
        }
    }, [getCurrentUserId]);

    // 停止轮询
    const stopPolling = useCallback(() => {
        if (null != pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        setIsSuspended(false);
        setInterrupted(false);
        pollingCountRef.current = 0;
    }, []);

    const resumeSessionSingleFlight = useCallback(async (sessionId: number): Promise<SessionStatus> => {
        const currentRequest = resumeRequestRef.current;
        if (currentRequest && currentRequest.sessionId === sessionId) {
            return currentRequest.promise;
        }
        const promise = chatService.resumeSession(sessionId)
            .finally(() => {
                if (resumeRequestRef.current?.sessionId === sessionId) {
                    resumeRequestRef.current = null;
                }
            });
        resumeRequestRef.current = {sessionId, promise};
        return promise;
    }, []);

    // 开始轮询
    const startPolling = useCallback((sessionId: number) => {
        stopPolling();
        pollingCountRef.current = 0;
        setIsSuspended(true);
        setPollingTick(0);

        pollingTimerRef.current = setInterval(async () => {
            pollingCountRef.current++;
            if (pollingCountRef.current > MAX_POLL_COUNT) {
                stopPolling();
                setState(prev => ({...prev, isLoading: false}));
                return;
            }
            try {
                let status = await chatService.getSessionStatus(sessionId);
                if (status.sessionRuntimeStatus === 'INTERRUPTED') {
                    try {
                        status = await resumeSessionSingleFlight(sessionId);
                    } catch (re) {
                        console.warn('[ChatPoll] resumeSession 失败 - sessionId:', sessionId, 'error:', re instanceof Error ? re.message : re);
                    }
                }
                const currentCount = messagesRef.current?.length || 0;

                // Chat异步化：新增增强字段检测逻辑（优先）
                if (null != status.sessionRuntimeStatus) {
                    const runtime = status.sessionRuntimeStatus;
                    setSessionRuntimeStatus(runtime || null);
                    const keepPolling = shouldKeepPollingRuntime(runtime);
                    setInterrupted(runtime === 'INTERRUPTED');
                    if (status.mainAgentCompleted) {
                        if (status.totalMessages > currentCount) {
                            const userId = getCurrentUserId();
                            const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                            setState(prev => ({...prev, messages}));
                            messagesRef.current = messages;
                        }
                        stopPolling();
                        setState(prev => ({...prev, isLoading: false}));
                        return;
                    }
                    // 检测处理完成
                    if (!keepPolling && !status.hasPendingWork) {
                        if (status.totalMessages > currentCount) {
                            const userId = getCurrentUserId();
                            const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                            setState(prev => ({...prev, messages}));
                            messagesRef.current = messages;
                        }
                        stopPolling();
                        setState(prev => ({...prev, isLoading: false}));
                        return;
                    }
                    // 仍有待处理工作：加载新消息（如有），包括INTERRUPTED恢复中
                    if (status.totalMessages > currentCount) {
                        const userId = getCurrentUserId();
                        const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                        setState(prev => ({...prev, messages}));
                        messagesRef.current = messages;
                    }
                    // 轮询中动态刷新子会话列表
                    try {
                        const children = await chatService.getChildSessions(sessionId);
                        setChildSessions(children);
                    } catch {
                        // 静默处理子会话刷新错误
                    }
                    setState(prev => ({...prev, isLoading: keepPolling}));
                    setPollingTick(prev => prev + 1);
                    return;
                }

                // 向后兼容：原有轮询逻辑（旧后端无增强字段）
                if (!status.isSuspended) {
                    if (status.totalMessages > currentCount) {
                        const userId = getCurrentUserId();
                        const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                        setState(prev => ({...prev, messages}));
                        messagesRef.current = messages;
                        const lastMsg = messages && messages.length > 0 ? messages[messages.length - 1] : null;
                        if (isRealAssistantMessage(lastMsg)) {
                            // 挂起解除且最后一条消息是模型回复 → 停止
                            stopPolling();
                            setState(prev => ({...prev, isLoading: false}));
                            return;
                        }
                    } else {
                        // 挂起解除且无新消息 → 安全停止
                        stopPolling();
                        setState(prev => ({...prev, isLoading: false}));
                        return;
                    }
                }
                // 主智能体已完成（mainAgentCompleted=true）— 无视 isSuspended 停残留，强制停止
                if (status.mainAgentCompleted) {
                    if (status.totalMessages > currentCount) {
                        const userId = getCurrentUserId();
                        const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                        setState(prev => ({...prev, messages, isLoading: false}));
                        messagesRef.current = messages;
                    } else {
                        setState(prev => ({...prev, isLoading: false}));
                    }
                    stopPolling();
                    return;
                }
                if (status.totalMessages > currentCount) {
                    const userId = getCurrentUserId();
                    const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                    setState(prev => ({...prev, messages}));
                    messagesRef.current = messages;
                }
                // 轮询中动态刷新子会话列表，确保用户能及时看到新增的子智能体会话入口
                try {
                    const children = await chatService.getChildSessions(sessionId);
                    setChildSessions(children);
                } catch {
                    console.warn('[ChatPoll] 刷新子会话失败', sessionId);
                }
                setPollingTick(prev => prev + 1);
            } catch (e) {
                console.warn('[ChatPoll] 轮询请求失败 - sessionId:', sessionId, 'error:', e instanceof Error ? e.message : e);
            }
        }, 3000);
    }, [stopPolling, getCurrentUserId, setChildSessions, resumeSessionSingleFlight]);

    // 切换会话
    const switchSession = useCallback(async (session: ChatSession) => {
        stopPolling();
        try {
            // 仅跳转子会话时保存当前会话到导航栈；切换到非子会话时清空栈
            setState(prev => {
                if ('CHILD' === session.sessionRole) {
                    if (null != prev.currentSession) {
                        setNavigationStack(stack => [...stack, prev.currentSession]);
                    }
                } else {
                    setNavigationStack([]);
                }
                return {...prev, isLoading: true, currentSession: session};
            });

            // 加载历史消息
            await loadMessages(session.sessionId);

            setState(prev => ({...prev, isLoading: false}));

            // 跳转子会话时启动轮询，保持根会话租约活跃
            try {
                const status = await resumeSessionSingleFlight(session.sessionId);
                if (shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                    startPolling(session.sessionId);
                }
            } catch {
                // 静默处理恢复状态查询错误
            }
        } catch (error) {
            const msg = (error as any)?.message || '';
            if (msg.includes('无权访问')) {
                // 会话已失效，从列表中移除
                setState(prev => ({
                    ...prev,
                    sessions: prev.sessions.filter(s => s.sessionId !== session.sessionId),
                    currentSession: prev.sessions.length > 1 ? prev.sessions.find(s => s.sessionId !== session.sessionId) ?? null : null,
                    error: '会话已失效',
                    isLoading: false
                }));
            } else {
                setState(prev => ({
                    ...prev,
                    error: '切换会话失败',
                    isLoading: false
                }));
            }
        }
    }, [loadMessages, stopPolling, startPolling, resumeSessionSingleFlight]);

    // 通过sessionId加载会话（用于从其他页面转移会话）
    const loadSessionById = useCallback(async (sessionId: number, userId?: string) => {
        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录'}));
            return;
        }
        try {
            setState(prev => ({...prev, isLoading: true}));

            // 加载会话消息
            await loadMessages(sessionId);

            // 尝试从sessions列表中找到该会话
            const targetSession = state.sessions.find(s => s.sessionId === sessionId);
            if (targetSession) {
                setState(prev => ({...prev, currentSession: targetSession}));
            } else {
                // 如果找不到，创建一个临时的会话对象，使用传入的userId
                const tempSession: ChatSession = {
                    sessionId: sessionId,
                    sessionName: `会话 ${sessionId}`,
                    userId: actualUserId,
                    status: 'active',
                    modelName: '',
                    createdTime: new Date().toISOString(),
                    updatedTime: new Date().toISOString()
                };
                setState(prev => ({...prev, currentSession: tempSession}));
            }

            setState(prev => ({...prev, isLoading: false}));

            // 检查会话是否需要恢复轮询（从外部转移过来的会话可能处于被中断状态）
            try {
                const status = await resumeSessionSingleFlight(sessionId);
                if (shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                    startPolling(sessionId);
                }
            } catch {
                // 静默处理恢复状态查询错误
            }
        } catch (error) {
            // console.error('加载会话失败:', error);
            setState(prev => ({
                ...prev,
                error: '加载会话失败',
                isLoading: false
            }));
        }
    }, [loadMessages, state.sessions, getCurrentUserId, startPolling, resumeSessionSingleFlight]);

    // 返回父会话
    const goBack = useCallback(async () => {
        if (0 === navigationStack.length) return;
        stopPolling();
        const parent = navigationStack[navigationStack.length - 1];
        setNavigationStack(prev => prev.slice(0, -1));
        let pollingStarted = false;
        try {
            setState(prev => ({...prev, isLoading: true, currentSession: parent}));
            await loadMessages(parent.sessionId);
            // 父会话可能仍处于挂起等待状态（子智能体还在执行），需要恢复轮询
            try {
                const status = await resumeSessionSingleFlight(parent.sessionId);
                if (shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                    startPolling(parent.sessionId);
                    pollingStarted = true;
                }
            } catch {
                // 恢复状态查询失败不影响主流程
            }
            if (!pollingStarted) {
                setState(prev => ({...prev, isLoading: false}));
            }
        } catch {
            setState(prev => ({...prev, isLoading: false}));
        }
    }, [navigationStack, stopPolling, loadMessages, startPolling, resumeSessionSingleFlight]);

    // 发送消息
    const sendMessage = useCallback(async (message: string, userId?: string, systemPrompt?: string) => {
        if (!message.trim()) {
            return;
        }

        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录'}));
            throw new Error('请先登录');
        }

        try {
            // 立即显示用户消息
            const userMessage = {
                messageId: Date.now(), // 临时ID
                sessionId: state.currentSession?.sessionId || 0,
                role: 'user' as const,
                content: message.trim(),
                tokensUsed: 0,
                processingTimeMs: 0,
                createdTime: new Date().toISOString()
            };

            setState(prev => ({
                ...prev,
                messages: [...(prev.messages || []), userMessage],
                isLoading: true,
                error: null
            }));

            let sessionId = state.currentSession?.sessionId;

            // 如果没有当前会话，创建一个新会话
            if (!sessionId) {
                const newSession = await createSession(undefined, actualUserId);
                sessionId = newSession.sessionId;
                setState(prev => ({...prev, currentSession: newSession}));
            }

            // 确保sessionId存在
            if (!sessionId) {
                throw new Error('无法获取有效的会话ID');
            }

            // 发送消息
            const response = await chatService.sendMessage({
                sessionId,
                message: message.trim(),
                userId: actualUserId,
                systemPrompt
            });

            // 异步模式：消息已入队，直接开始轮询等待处理完成
            if (response.queued && response.sessionId) {
                setInterrupted(false);
                startPolling(response.sessionId);
                setState(prev => ({...prev, isLoading: true}));
                return;
            }

            let shouldKeepLoading = false;

            if (response.success && response.sessionId) {
                // 同步回退模式：重新加载消息以获取AI回复（会替换临时用户消息）
                const msgs = await loadMessages(response.sessionId);

                const lastMsg = msgs && msgs.length > 0 ? msgs[msgs.length - 1] : null;
                const hasRealAssistant = isRealAssistantMessage(lastMsg);
                if (!hasRealAssistant) {
                    shouldKeepLoading = true;
                } else {
                    try {
                        const status = await chatService.getSessionStatus(response.sessionId);
                        if (status.isSuspended) {
                            shouldKeepLoading = true;
                        }
                    } catch {
                        // 状态查询失败，依赖消息判断
                    }
                }

                if (shouldKeepLoading) {
                    startPolling(response.sessionId);
                }

                // 局部更新会话名称，避免全量拉取会话列表
                // 只在第一条消息发送后检查（sessionName可能从"新对话"变为实际名称）
                const currentSessionName = state.currentSession?.sessionName;
                if (currentSessionName?.startsWith('新对话') || currentSessionName?.startsWith('默认对话')) {
                    // 通过WebSocket或直接查询获取更新后的sessionName
                    // 这里使用轻量级的latest接口获取最新会话，减少数据传输
                    try {
                        const userId = getCurrentUserId();
                        if (userId) {
                            const latestSession = await chatService.getLatestSession(userId);
                            if (latestSession && latestSession.sessionId === response.sessionId
                                && latestSession.sessionName !== currentSessionName) {
                                // 仅更新currentSession和sessions列表中对应的项
                                setState(prev => ({
                                    ...prev,
                                    currentSession: latestSession,
                                    sessions: prev.sessions.map(s =>
                                        s.sessionId === latestSession.sessionId ? latestSession : s
                                    )
                                }));
                            }
                        }
                    } catch {
                        // 忽略刷新会话名称的错误，不影响主流程
                    }
                }
            }

            if (shouldKeepLoading) {
                setState(prev => ({...prev, isLoading: true}));
            }
        } catch (error) {
            // console.error('发送消息失败:', error);
            setState(prev => ({
                ...prev,
                error: '发送消息失败',
                isLoading: false
            }));
            throw error;
        }
    }, [state.currentSession, createSession, loadMessages, startPolling, getCurrentUserId]);

    // 删除会话
    const deleteSession = useCallback(async (sessionId: number, userId?: string) => {
        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录'}));
            return;
        }
        try {
            await chatService.deleteSession(sessionId, actualUserId);

            setState(prev => ({
                ...prev,
                sessions: prev.sessions.filter(s => s.sessionId !== sessionId),
                currentSession: prev.currentSession?.sessionId === sessionId ? null : prev.currentSession,
                messages: prev.currentSession?.sessionId === sessionId ? [] : (prev.messages || [])
            }));
        } catch (error) {
            // console.error('删除会话失败:', error);
            setState(prev => ({
                ...prev,
                error: '删除会话失败'
            }));
            throw error;
        }
    }, [getCurrentUserId]);

    // 更新会话名称
    const updateSessionName = useCallback(async (sessionId: number, sessionName: string, userId?: string) => {
        const actualUserId = userId || getCurrentUserId();
        if (!actualUserId) {
            setState(prev => ({...prev, error: '请先登录'}));
            return;
        }
        try {
            const updatedSession = await chatService.updateSessionName(sessionId, sessionName, actualUserId);

            setState(prev => ({
                ...prev,
                sessions: prev.sessions.map(s =>
                    s.sessionId === sessionId ? updatedSession : s
                ),
                currentSession: prev.currentSession?.sessionId === sessionId ? updatedSession : prev.currentSession
            }));
        } catch (error) {
            // console.error('更新会话名称失败:', error);
            setState(prev => ({
                ...prev,
                error: '更新会话名称失败'
            }));
            throw error;
        }
    }, [getCurrentUserId]);

    // 清除错误
    const clearError = useCallback(() => {
        setState(prev => ({...prev, error: null}));
    }, []);

    // 初始化
    useEffect(() => {
        const initializeChat = async () => {
            // 直接使用 user?.id 避免依赖 getCurrentUserId 函数
            const userId = user?.id ? String(user.id) : null;

            // 未登录时不初始化
            if (!userId) {
                setState(prev => ({
                    ...prev,
                    error: '请先登录后再使用AI助手',
                    isLoading: false
                }));
                return;
            }

            try {
                setState(prev => ({...prev, isLoading: true}));

                // 加载当前用户的会话列表（分页），默认查询NORMAL类型
                const pageResult = await chatService.getUserSessions(userId, undefined, 'NORMAL', 1, 20);
                setState(prev => ({...prev, sessions: pageResult.records}));

                // 尝试从localStorage恢复上次使用的会话
                try {
                    const savedSession = localStorage.getItem('currentChatSession');
                    if (savedSession) {
                        const sessionData = JSON.parse(savedSession);
                        const restoredSession = pageResult.records.find(s => s.sessionId === sessionData.sessionId);
                        if (restoredSession) {
                            setState(prev => ({...prev, currentSession: restoredSession}));
                            await loadMessages(restoredSession.sessionId);
                            // 用户返回会话时，检查是否需要恢复轮询
                            try {
                                const status = await resumeSessionSingleFlight(restoredSession.sessionId);
                                if (shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                                    startPolling(restoredSession.sessionId);
                                }
                            } catch {
                                // 静默处理恢复状态查询错误
                            }
                            return;
                        }
                    }
                } catch (error) {
                    // console.log('恢复会话失败:', error);
                }

                // 如果没有恢复的会话，使用用户的最新会话
                if (pageResult.records.length > 0) {
                    const latestSession = pageResult.records[0];
                    setState(prev => ({...prev, currentSession: latestSession}));
                    await loadMessages(latestSession.sessionId);
                    // 用户返回会话时，检查是否需要恢复轮询
                    try {
                        const status = await resumeSessionSingleFlight(latestSession.sessionId);
                        if (shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                            startPolling(latestSession.sessionId);
                        }
                    } catch {
                        // 静默处理恢复状态查询错误
                    }
                } else {
                    // 如果没有会话，创建一个新的
                    const newSession = await chatService.createNewSession(undefined, userId);
                    setState(prev => ({
                        ...prev,
                        sessions: [newSession],
                        currentSession: newSession
                    }));
                }
            } catch (error) {
                // console.error('初始化聊天失败:', error);
                setState(prev => ({
                    ...prev,
                    error: '初始化聊天失败',
                    isLoading: false
                }));
            }
        };

        initializeChat();
    }, [user?.id, loadMessages, startPolling, resumeSessionSingleFlight]);

    // 持久化currentSession到localStorage
    useEffect(() => {
        if (state.currentSession) {
            localStorage.setItem('currentChatSession', JSON.stringify({
                sessionId: state.currentSession.sessionId,
                userId: state.currentSession.userId,
                sessionName: state.currentSession.sessionName
            }));
        } else {
            localStorage.removeItem('currentChatSession');
        }
    }, [state.currentSession]);

    // 独立 lease 心跳：与消息轮询解耦，只为活跃执行态会话保持租约。
    // 会话回到 IDLE 且无待处理工作时停止续租，避免对失效/空闲会话反复轮询（降低后端负担与慢日志）。
    // 用户再次发送消息时 startPolling 会重新 getSessionStatus 并续租，租约自动恢复。
    useEffect(() => {
        const sessionId = state.currentSession?.sessionId;
        if (!sessionId) {
            return;
        }

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | null = null;

        const stopHeartbeat = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const heartbeat = async () => {
            if (cancelled) {
                return;
            }
            try {
                const status = await chatService.getSessionStatus(sessionId);
                if (cancelled) {
                    return;
                }
                // 空闲且无待处理工作：租约不再需要，停止心跳
                if (status.sessionRuntimeStatus === 'IDLE' && !status.hasPendingWork) {
                    stopHeartbeat();
                }
            } catch {
                // lease 刷新失败静默，下次心跳继续
            }
        };

        void heartbeat();
        intervalId = setInterval(() => {
            if (!cancelled) {
                void heartbeat();
            }
        }, 8000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [state.currentSession?.sessionId, getCurrentUserId]);

    useEffect(() => {
        const becameVisible = isPageVisible && !previousPageVisibleRef.current;
        previousPageVisibleRef.current = isPageVisible;
        if (!becameVisible) {
            return;
        }
        const sessionId = state.currentSession?.sessionId;
        if (!sessionId || null != pollingTimerRef.current) {
            return;
        }

        let cancelled = false;
        const resumeCurrentSession = async () => {
            try {
                const status = await resumeSessionSingleFlight(sessionId);
                if (cancelled) {
                    return;
                }
                setSessionRuntimeStatus(status.sessionRuntimeStatus || null);
                setInterrupted(status.sessionRuntimeStatus === 'INTERRUPTED');
                if (status.totalMessages > (messagesRef.current?.length || 0)) {
                    const userId = getCurrentUserId();
                    const messages = await chatService.getSessionMessages(sessionId, userId || undefined);
                    if (!cancelled) {
                        setState(prev => ({...prev, messages}));
                        messagesRef.current = messages;
                    }
                }
                if (!cancelled && shouldKeepPollingRuntime(status.sessionRuntimeStatus)) {
                    startPolling(sessionId);
                }
            } catch {
                // 静默处理前台恢复失败
            }
        };

        void resumeCurrentSession();
        return () => {
            cancelled = true;
        };
    }, [isPageVisible, state.currentSession, getCurrentUserId, startPolling, resumeSessionSingleFlight]);

    messagesRef.current = state.messages;

    // 组件卸载时清理轮询定时器
    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    return {
        ...state,
        loadSessions,
        loadMoreSessions,
        hasMoreSessions,
        loadingMore,
        childSessions,
        loadChildSessions,
        createSession,
        loadMessages,
        switchSession,
        loadSessionById,
        sendMessage,
        deleteSession,
        updateSessionName,
        clearError,
        isSuspended,
        interrupted,
        sessionRuntimeStatus,
        pollingTick,
        canGoBack: navigationStack.length > 0,
        goBack,
        startPolling,
        stopPolling
    };
};
